import {
  abortUpload,
  completeUpload,
  createUpload,
  getUploadStatus,
  signParts,
  uploadErrorMessage,
  type UploadedPart,
} from "./uploadsApi";
import { deleteUploadFile, loadUploadFile, saveUploadFile } from "./uploadFileStore";

/**
 * Browser side of HTPTV2-89's multipart uploads. It lives at module scope,
 * not in a component, so an upload keeps going when the admin navigates
 * elsewhere in the app (YouTube-style): the scheduler page just subscribes.
 *
 * Bytes go straight to S3 with presigned part URLs; the API only brokers.
 * Each finished part's ETag is persisted, because completing a multipart
 * upload needs every part's ETag and the status endpoint only reports part
 * numbers. The File itself is kept in IndexedDB, so after a reload or a
 * closed tab the upload resumes by itself (autoResumeUploads) and only the
 * parts S3 does not have yet are sent. Where the browser could not store
 * the file, the card falls back to asking for the same file again.
 */

export type UploadPhase = "uploading" | "completing" | "done" | "error" | "paused";

export interface LocalUpload {
  assetId: string;
  title: string;
  fileName: string;
  fileSize: number;
  loadedBytes: number;
  phase: UploadPhase;
  error: string | null;
}

interface Persisted {
  title: string;
  fileName: string;
  fileSize: number;
  lastModified: number;
  partSize: number;
  totalParts: number;
  etags: Record<string, string>;
}

const STORAGE_KEY = "htp.scheduler.uploads";
/** Parallel part PUTs. S3 speaks HTTP/1.1 and browsers open about six
 * connections per host, so six keeps every connection busy; more would
 * only queue in the browser. */
export const CONCURRENCY = 6;
/** The API signs at most this many part URLs per call. */
const SIGN_BATCH = 20;

function readPersisted(): Record<string, Persisted> {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}") as Record<string, Persisted>;
  } catch {
    return {};
  }
}

function writePersisted(all: Record<string, Persisted>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch {
    // Storage full or blocked: the upload still runs, it just cannot resume.
  }
}

function updatePersisted(assetId: string, fn: (p: Persisted | undefined) => Persisted | undefined) {
  const all = readPersisted();
  const next = fn(all[assetId]);
  if (next) all[assetId] = next;
  else delete all[assetId];
  writePersisted(all);
}

// --- observable store --------------------------------------------------

let uploads: Record<string, LocalUpload> = {};
const listeners = new Set<() => void>();
const settledListeners = new Set<(assetId: string) => void>();
const cancelled = new Set<string>();

function emit() {
  listeners.forEach((l) => l());
}

function patch(assetId: string, fields: Partial<LocalUpload>) {
  const current = uploads[assetId];
  if (!current) return;
  uploads = { ...uploads, [assetId]: { ...current, ...fields } };
  emit();
}

export function subscribeUploads(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getUploadsSnapshot(): Record<string, LocalUpload> {
  return uploads;
}

/** Fires when an upload finishes (or fails), so lists can refetch. */
export function onUploadSettled(listener: (assetId: string) => void): () => void {
  settledListeners.add(listener);
  return () => settledListeners.delete(listener);
}

/** Asset ids this browser started but has not finished: the rows it can
 * offer to resume after a reload. */
export function resumableAssetIds(): string[] {
  return Object.keys(readPersisted());
}

export function isUploadActive(): boolean {
  return Object.values(uploads).some((u) => u.phase === "uploading" || u.phase === "completing");
}

// --- engine ------------------------------------------------------------

/** Backoff between attempts at one part: a flaky connection gets several
 * tries before the upload stops and waits for a resume. */
export const PART_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 16000];

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Resolves once the browser is online (immediately when it already is). */
function whenOnline(): Promise<void> {
  if (typeof navigator === "undefined" || navigator.onLine !== false) return Promise.resolve();
  return new Promise((resolve) => window.addEventListener("online", () => resolve(), { once: true }));
}

/** putPartOnce with retries: waits out an offline spell, then backs off. */
async function putPart(url: string, blob: Blob, onProgress: (loaded: number) => void): Promise<string> {
  for (let attempt = 0; ; attempt++) {
    await whenOnline();
    try {
      return await putPartOnce(url, blob, onProgress);
    } catch (e) {
      if (attempt >= PART_RETRY_DELAYS_MS.length) throw e;
      onProgress(0);
      await sleep(PART_RETRY_DELAYS_MS[attempt]);
    }
  }
}

/** One part PUT through XHR (fetch has no upload progress). Resolves to the
 * ETag S3 returns, which the bucket's CORS rule exposes. */
function putPartOnce(url: string, blob: Blob, onProgress: (loaded: number) => void): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.upload.onprogress = (e) => onProgress(e.loaded);
    xhr.onload = () => {
      const etag = xhr.getResponseHeader("ETag");
      if (xhr.status >= 200 && xhr.status < 300 && etag) resolve(etag);
      else reject(new Error(`Part upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error("Network error while uploading"));
    xhr.send(blob);
  });
}

async function run(assetId: string, file: File, partSize: number, totalParts: number, done: Set<number>) {
  const partBytes = (n: number) => Math.min(partSize, file.size - (n - 1) * partSize);
  const inFlight = new Map<number, number>();
  let finishedBytes = [...done].reduce((sum, n) => sum + partBytes(n), 0);
  const report = () => {
    let live = 0;
    inFlight.forEach((v) => (live += v));
    patch(assetId, { loadedBytes: finishedBytes + live });
  };
  report();

  const todo: number[] = [];
  for (let n = 1; n <= totalParts; n++) if (!done.has(n)) todo.push(n);

  try {
    // Presigned URLs are signed a batch at a time (they expire, and a big
    // file has many parts), but pipelined: one worker pool runs through
    // every part, and the next batch is requested as soon as the current
    // one starts, so the pool never idles waiting on a signing call.
    const batches: number[][] = [];
    for (let i = 0; i < todo.length; i += SIGN_BATCH) batches.push(todo.slice(i, i + SIGN_BATCH));
    const signed = new Map<number, Promise<Record<string, string>>>();
    const signBatch = (b: number) => {
      if (b >= batches.length) return undefined;
      if (!signed.has(b)) signed.set(b, signParts(assetId, batches[b]));
      return signed.get(b);
    };
    const urlFor = async (index: number, n: number) => {
      const b = Math.floor(index / SIGN_BATCH);
      void signBatch(b + 1)?.catch(() => undefined); // prefetch the next batch
      const urls = await signBatch(b)!;
      return urls[String(n)];
    };

    let next = 0;
    const worker = async () => {
      while (next < todo.length) {
        if (cancelled.has(assetId)) throw new Error("cancelled");
        const index = next++;
        const n = todo[index];
        const url = await urlFor(index, n);
        const blob = file.slice((n - 1) * partSize, (n - 1) * partSize + partBytes(n));
        inFlight.set(n, 0);
        const etag = await putPart(url, blob, (loaded) => {
          inFlight.set(n, loaded);
          report();
        });
        inFlight.delete(n);
        finishedBytes += partBytes(n);
        updatePersisted(assetId, (p) => (p ? { ...p, etags: { ...p.etags, [n]: etag } } : p));
        report();
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, todo.length) }, worker));

    patch(assetId, { phase: "completing" });
    const etags = readPersisted()[assetId]?.etags ?? {};
    const parts: UploadedPart[] = [];
    for (let n = 1; n <= totalParts; n++) parts.push({ PartNumber: n, ETag: etags[n] });
    await completeUpload(assetId, parts);
    updatePersisted(assetId, () => undefined);
    await deleteUploadFile(assetId);
    patch(assetId, { phase: "done", loadedBytes: file.size });
  } catch (e) {
    if (cancelled.has(assetId)) return;
    // The session and every finished part survive on S3; the row offers a
    // resume instead of starting over.
    patch(assetId, { phase: "error", error: uploadErrorMessage(e, "Upload failed") });
  } finally {
    settledListeners.forEach((l) => l(assetId));
  }
}

/** Start a new upload: opens the session, then streams the parts. */
export async function startUpload(file: File, title: string, description: string | null): Promise<string> {
  const created = await createUpload({
    title,
    description,
    fileName: file.name,
    fileSize: file.size,
    mimeType: file.type || (file.name.toLowerCase().endsWith(".mov") ? "video/quicktime" : "video/mp4"),
  });
  updatePersisted(created.assetId, () => ({
    title,
    fileName: file.name,
    fileSize: file.size,
    lastModified: file.lastModified,
    partSize: created.partSizeBytes,
    totalParts: created.totalParts,
    etags: {},
  }));
  // Kept for auto-resume; the upload starts regardless of whether the
  // browser agreed to store it.
  void saveUploadFile(created.assetId, file);
  uploads = {
    ...uploads,
    [created.assetId]: {
      assetId: created.assetId,
      title,
      fileName: file.name,
      fileSize: file.size,
      loadedBytes: 0,
      phase: "uploading",
      error: null,
    },
  };
  emit();
  void run(created.assetId, file, created.partSizeBytes, created.totalParts, new Set());
  return created.assetId;
}

/**
 * Continue an interrupted upload with the file the admin re-picked. The
 * file must be the same one (name and size); only parts that S3 is missing,
 * or whose ETag this browser lost, are sent again.
 */
export async function resumeUpload(assetId: string, file: File, title: string): Promise<void> {
  const status = await getUploadStatus(assetId);
  if (status.status !== "uploading") throw new UploadNotResumableError();
  const persisted = readPersisted()[assetId];
  const expectedName = persisted?.fileName ?? status.fileName;
  const expectedSize = persisted?.fileSize ?? status.sizeBytes;
  if (file.name !== expectedName || file.size !== expectedSize) {
    throw new Error(`Pick the original file: ${expectedName}.`);
  }
  const partSize = status.partSizeBytes ?? persisted?.partSize ?? 8 * 1024 * 1024;
  const totalParts = status.totalParts ?? persisted?.totalParts ?? Math.ceil(file.size / partSize);
  const etags = persisted?.etags ?? {};
  const done = new Set((status.uploadedParts ?? []).filter((n) => Boolean(etags[n])));
  updatePersisted(assetId, (p) => ({
    title,
    fileName: file.name,
    fileSize: file.size,
    lastModified: file.lastModified,
    partSize,
    totalParts,
    etags: p?.etags ?? {},
  }));
  cancelled.delete(assetId);
  uploads = {
    ...uploads,
    [assetId]: {
      assetId,
      title,
      fileName: file.name,
      fileSize: file.size,
      loadedBytes: 0,
      phase: "uploading",
      error: null,
    },
  };
  emit();
  void run(assetId, file, partSize, totalParts, done);
}

/** The server no longer holds this upload open (finished, cancelled or
 * expired): resuming is impossible, not just temporarily failing. */
export class UploadNotResumableError extends Error {
  constructor() {
    super("This upload can no longer be resumed.");
    this.name = "UploadNotResumableError";
  }
}

let autoResumeStarted = false;

/**
 * Resume with the File kept in IndexedDB, no file picker involved. Resolves
 * false when this browser has no stored copy (the caller then asks the
 * admin for the file).
 */
export async function retryUpload(assetId: string, title?: string): Promise<boolean> {
  const file = await loadUploadFile(assetId);
  if (!file) return false;
  const name = title ?? uploads[assetId]?.title ?? readPersisted()[assetId]?.title ?? file.name;
  await resumeUpload(assetId, file, name);
  return true;
}

/**
 * Pick every interrupted upload back up by itself, using the File kept in
 * IndexedDB. Runs once per page load (the uploads library calls it on
 * mount). An upload the server no longer considers open, finished,
 * cancelled elsewhere, or expired, is forgotten locally instead.
 */
export async function autoResumeUploads(): Promise<void> {
  if (autoResumeStarted) return;
  autoResumeStarted = true;
  // An upload that ran out of retries while the connection was down picks
  // itself back up as soon as the browser is online again.
  if (typeof window !== "undefined") {
    window.addEventListener("online", () => {
      for (const u of Object.values(uploads)) {
        if (u.phase === "error") void retryUpload(u.assetId).catch(() => undefined);
      }
    });
  }
  const persisted = readPersisted();
  for (const [assetId, entry] of Object.entries(persisted)) {
    if (uploads[assetId]) continue;
    try {
      if (!(await retryUpload(assetId, entry.title))) continue; // Not stored: manual resume.
    } catch (e) {
      // Only a definitive answer forgets the upload; a network blip here
      // leaves it for the next page load or the manual resume.
      if (e instanceof UploadNotResumableError) {
        updatePersisted(assetId, () => undefined);
        await deleteUploadFile(assetId);
      }
    }
  }
}

/** Cancel an upload in progress: stops the workers and drops the session. */
export async function cancelUpload(assetId: string): Promise<void> {
  cancelled.add(assetId);
  updatePersisted(assetId, () => undefined);
  void deleteUploadFile(assetId);
  const next = { ...uploads };
  delete next[assetId];
  uploads = next;
  emit();
  await abortUpload(assetId);
}

/** Test seam: reset module state between tests. */
export function __resetUploadManager(): void {
  autoResumeStarted = false;
  uploads = {};
  cancelled.clear();
  listeners.clear();
  settledListeners.clear();
}
